import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/swal';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';

const UserForm = () => {
  const { user } = useAuth();
  const { canCreate, canEdit } = usePermission();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    department_id: '',
    is_manager: false,
    status: 'active',
  });

  useEffect(() => {
    if (isEditMode ? !canEdit('users') : !canCreate('users')) {
      navigate('/');
      return;
    }

    fetchDepartments();
    if (isEditMode) {
      fetchUser();
    }
  }, [id]);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Departmanlar yüklenemedi:', error);
      showError('Departmanlar yüklenirken bir hata oluştu');
    }
  };

  const fetchUser = async () => {
    try {
      const response = await api.get(`/users/${id}`);
      const user = response.data;
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        password_confirmation: '',
        department_id: user.department_id || '',
        is_manager: user.is_manager || false,
        status: user.status || 'active',
      });
    } catch (error) {
      console.error('Kullanıcı yüklenemedi:', error);
      showError('Kullanıcı yüklenirken bir hata oluştu');
      navigate('/departments/users');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Şifre kontrolü
    if (!isEditMode && !formData.password) {
      showError('Şifre zorunludur');
      setLoading(false);
      return;
    }

    if (formData.password && formData.password !== formData.password_confirmation) {
      showError('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }

    try {
      const data = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        department_id: formData.department_id,
        is_manager: formData.is_manager,
        status: formData.status,
      };

      // Şifre varsa ekle
      if (formData.password) {
        data.password = formData.password;
      }

      if (isEditMode) {
        await api.put(`/users/${id}`, data);
        showSuccess('Kullanıcı başarıyla güncellendi');
      } else {
        await api.post('/users', data);
        showSuccess('Kullanıcı başarıyla oluşturuldu');
      }
      navigate('/departments/users');
    } catch (error) {
      console.error('Kullanıcı kaydedilemedi:', error);
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        showError(errors.join('\n'));
      } else {
        showError('Kullanıcı kaydedilirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Button variant="secondary" size="sm" onClick={() => navigate('/departments/users')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
      </div>

      <Card title={isEditMode ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Input
              label="Ad Soyad"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Örn: Ahmet Yılmaz"
              required
            />
            <Input
              label="E-posta"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="ornek@email.com"
              required
            />
            <Input
              label="Telefon"
              name="phone"
              type="text"
              value={formData.phone}
              onChange={handleChange}
              placeholder="05XX XXX XX XX"
            />
            <Input
              label={isEditMode ? "Yeni Şifre (Boş bırakılırsa değişmez)" : "Şifre"}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              required={!isEditMode}
            />
            <Input
              label="Şifre Tekrar"
              name="password_confirmation"
              type="password"
              value={formData.password_confirmation}
              onChange={handleChange}
              placeholder="••••••••"
              required={!isEditMode && formData.password}
            />
            <Select
              label="Departman"
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              options={departments.map(dept => ({
                value: dept.id,
                label: dept.name
              }))}
              required
            />
            <Select
              label="Durum"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={[
                { value: 'active', label: 'Aktif' },
                { value: 'inactive', label: 'Pasif' },
              ]}
              required
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_manager"
                checked={formData.is_manager}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Yönetici</span>
            </label>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={loading}>
              {loading ? 'Kaydediliyor...' : isEditMode ? 'Güncelle' : 'Kaydet'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/departments/users')}
            >
              İptal
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default UserForm;
